import React from 'react';
import { Info } from 'lucide-react';
import type { VideoEmbedData } from './types';

interface VideoTabProps {
  data: VideoEmbedData;
  onChange: (field: string, value: any) => void;
  onVideoUrlPaste: (url: string) => void;
}

export const VideoTab: React.FC<VideoTabProps> = ({ data, onChange, onVideoUrlPaste }) => {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Video Embed Helper</p>
            <p>
              Embed videos from YouTube or Vimeo with additional controls. Paste the video URL or ID
              below.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Platform *</label>
        <select
          value={data.platform}
          onChange={e => onChange('platform', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
          <option value="dailymotion">Dailymotion</option>
          <option value="twitch">Twitch</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL or ID *</label>
        <input
          type="text"
          value={data.videoId}
          onChange={e => {
            const value = e.target.value;
            onChange('videoId', value);
            // Try to extract ID if it looks like a URL
            if (value.includes('http')) {
              onVideoUrlPaste(value);
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="dQw4w9WgXcQ or https://www.youtube.com/watch?v=dQw4w9WgXcQ"
        />
        <p className="mt-1 text-xs text-gray-500">Paste the full URL or just the video ID</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
          <select
            value={data.aspectRatio}
            onChange={e => onChange('aspectRatio', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="16:9">16:9 (Widescreen)</option>
            <option value="4:3">4:3 (Standard)</option>
            <option value="1:1">1:1 (Square)</option>
            <option value="9:16">9:16 (Vertical)</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.autoplay}
            onChange={e => onChange('autoplay', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Autoplay</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.muted}
            onChange={e => onChange('muted', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Muted</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.controls}
            onChange={e => onChange('controls', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Show Controls</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={data.loop}
            onChange={e => onChange('loop', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Loop</span>
        </label>
      </div>
    </div>
  );
};
